import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Checkbox, FormControl, FormControlLabel, FormLabel, MenuItem, TextField, Tooltip } from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers-pro';

import { Dayjs, toDayJs } from '~/services/TimeService';

export interface FieldOption {
  label: string;
  value: string;
}

export interface ReadOnlyFieldConfig {
  control?: 'checkbox' | 'checklist';
  helperText?: string;
  label: string;
  layout?: {
    column?: 1 | 2;
    columns?: 1 | 2 | 3 | 4;
    compact?: boolean;
    group?: string;
    inline?: boolean;
    span?: 'full';
  };
  multiline?: boolean;
  options?: Array<FieldOption>;
  readOnly?: boolean;
  required?: boolean;
  type?: React.InputHTMLAttributes<HTMLInputElement>['type'];
  value: string;
}

interface Props {
  field: ReadOnlyFieldConfig;
  onChange?: (value: string) => void;
  value?: string;
}

function getPickerValue(value: string): Dayjs | null {
  if (!value) {
    return null;
  }

  const dayJsValue = toDayJs(value);
  return dayJsValue.isValid() ? dayJsValue : null;
}

const requiredAsteriskSx = {
  '& .MuiFormLabel-asterisk': {
    color: 'error.main',
  },
};

export default function ReadOnlyField({ field, onChange, value }: Props) {
  const isReadOnly = field.readOnly ?? true;
  const fieldValue = value ?? field.value;
  const hasCurrentSelectOption = field.options?.some((option) => option.value === fieldValue);
  const shouldShrinkLabel =
    Boolean(fieldValue) || Boolean(field.helperText) || field.type === 'date' || field.type === 'datetime-local';
  const label = field.helperText ? (
    <Box component="span" sx={{ alignItems: 'center', display: 'inline-flex', gap: 0.5 }}>
      {field.label}
      <Tooltip title={field.helperText}>
        <InfoOutlinedIcon color="disabled" sx={{ fontSize: 14 }} />
      </Tooltip>
    </Box>
  ) : (
    field.label
  );
  if (field.control === 'checkbox') {
    return (
      <FormControl disabled={isReadOnly}>
        <FormControlLabel
          control={
            <Checkbox
              checked={fieldValue === 'Yes'}
              size="small"
              onChange={(event) => onChange?.(event.target.checked ? 'Yes' : 'No')}
            />
          }
          label={label}
          sx={{ m: 0 }}
        />
      </FormControl>
    );
  }

  if (field.control === 'checklist') {
    const selectedValues = new Set(
      fieldValue
        .split(',')
        .map((selectedValue) => selectedValue.trim())
        .filter(Boolean),
    );

    return (
      <FormControl disabled={isReadOnly}>
        <FormLabel
          required={field.required}
          sx={{
            ...requiredAsteriskSx,
            mb: 0.5,
            typography: 'caption',
          }}
        >
          {label}
        </FormLabel>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {field.options?.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={selectedValues.has(option.value)}
                  size="small"
                  onChange={(event) => {
                    const nextValues = new Set(selectedValues);
                    if (event.target.checked) {
                      nextValues.add(option.value);
                    } else {
                      nextValues.delete(option.value);
                    }
                    onChange?.(Array.from(nextValues).join(', '));
                  }}
                />
              }
              label={option.label}
              sx={{ m: 0 }}
            />
          ))}
        </Box>
      </FormControl>
    );
  }

  if (field.options) {
    return (
      <TextField
        disabled={isReadOnly}
        fullWidth
        select
        label={label}
        required={field.required}
        size="small"
        sx={requiredAsteriskSx}
        value={fieldValue}
        onChange={(event) => onChange?.(event.target.value)}
        slotProps={{
          inputLabel: { shrink: shouldShrinkLabel },
        }}
      >
        {!fieldValue && <MenuItem value="">Not available</MenuItem>}
        {fieldValue && !hasCurrentSelectOption && <MenuItem value={fieldValue}>{fieldValue}</MenuItem>}
        {field.options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (field.type === 'date') {
    return (
      <DatePicker
        disabled={isReadOnly}
        label={label}
        readOnly={isReadOnly}
        slotProps={{
          textField: {
            fullWidth: true,
            required: field.required,
            size: 'small',
            sx: requiredAsteriskSx,
          },
        }}
        value={getPickerValue(fieldValue)}
        onChange={(date) => onChange?.(date?.isValid() ? date.format('YYYY-MM-DD') : '')}
      />
    );
  }

  if (field.type === 'datetime-local') {
    return (
      <DateTimePicker
        disabled={isReadOnly}
        label={label}
        readOnly={isReadOnly}
        slotProps={{
          textField: {
            fullWidth: true,
            required: field.required,
            size: 'small',
            sx: requiredAsteriskSx,
          },
        }}
        value={getPickerValue(fieldValue)}
        onChange={(date) => onChange?.(date?.isValid() ? date.format('YYYY-MM-DDTHH:mm') : '')}
      />
    );
  }

  return (
    <TextField
      fullWidth
      label={label}
      minRows={field.multiline ? 3 : undefined}
      multiline={field.multiline}
      required={field.required}
      size="small"
      sx={{
        ...requiredAsteriskSx,
        ...(field.multiline && {
          '& .MuiInputBase-inputMultiline': {
            resize: 'vertical',
          },
        }),
      }}
      type={field.type}
      value={fieldValue}
      onChange={(event) => onChange?.(event.target.value)}
      slotProps={{
        htmlInput: { 'aria-readonly': isReadOnly },
        input: { readOnly: isReadOnly },
        inputLabel: { shrink: shouldShrinkLabel },
      }}
    />
  );
}
